import { ComponentProps } from 'react';

import { cn } from './cn';
import { ButtonPropsSizeType, ColorValue } from './common.type';

export const hashCode = (str: string, mod: number): number => {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = (hash << 5) - hash + char;
    hash = hash & hash; // Convert to 32bit integer
  }
  return Math.abs(hash) % mod;
};

export const getBgColor = (
  s: string,
  colors: string[] = [
    'linear-gradient(45deg, #42a11f, #2dca1f)',
    'linear-gradient(45deg, #73173f, #ca1f68)',
    'linear-gradient(45deg, #99aa38, #3e761a)',
    'linear-gradient(45deg, #25145e, #3910bc)',
    'linear-gradient(45deg, #621e76, #9f1fca)',
    'linear-gradient(45deg, #a92e2e, #ec2727)',
  ],
) => {
  const colorIndex = s != null && typeof s === 'string' ? hashCode(s, colors.length) : undefined;
  return colors[colorIndex ?? 0];
};

export const isNotEmpty = (s: string | null | undefined): s is string => s != null && s.length > 0;

export const extractSizeFromClassName = (className?: string): number | 'full' | undefined => {
  // Check for responsive sizing
  if (className?.includes('size-full')) return 'full';

  const value = parseFloat(className?.match(/size-(\d+)/)?.[1] || '');
  return isNaN(value) ? undefined : Math.round(value * 4);
};

export function buttonMinHeight(size?: ButtonPropsSizeType) {
  return {
    'min-h-13': size == 'xl',
    'min-h-11': size == 'lg',
    'min-h-9': size == null || size == 'base',
    'min-h-7': size === 'sm',
    'min-h-5': size === 'xs',
  };
}
export function buttonHeight(size?: ButtonPropsSizeType) {
  return {
    'h-13': size == 'xl',
    'h-11': size == 'lg',
    'h-9': size == null || size == 'base',
    'h-7': size === 'sm',
    'h-5': size === 'xs',
  };
}

export function buttonHeightAndBorder(size?: ButtonPropsSizeType) {
  return {
    ...buttonHeight(size),
    ...buttonTextAndBorder(size),
  };
}

export function buttonRounded(size?: ButtonPropsSizeType) {
  return {
    'rounded-2xl': size == 'xl',
    'rounded-xl': size == 'lg',
    'rounded-lg': size == 'base' || size == null,
    'rounded-md': size === 'sm',
    'rounded-sm': size === 'xs',
  };
}

export function buttonText(size: ButtonPropsSizeType = 'base') {
  return {
    'text-xl': size == 'xl',
    'text-lg': size == 'lg',
    'text-base': size == 'base',
    'text-xs': size === 'sm',
    'text-2xs': size === 'xs',
  };
}

export function buttonTextAndBorder(size?: ButtonPropsSizeType) {
  return {
    ...buttonText(size),
    ...buttonRounded(size),
    ...buttonMinHeight(size),
  };
}

export function buttonHPadding(size: ButtonPropsSizeType = 'base') {
  return {
    'first:pl-4 last:pr-4': size == 'xl',
    'first:pl-3.5 last:pr-3.5': size == 'lg',
    'first:pl-3 last:pr-3': size == 'base',
    'first:pl-2 last:pr-2': size == 'sm',
    'first:pl-1.5 last:pr-1.5': size == 'xs',
  };
}

export function buttonCommonClassName({
  size,
  disabled,
  type,
  active,
  text,
  className,
  clickable,
}: {
  size?: ButtonPropsSizeType;
  disabled?: boolean;
  type?: ComponentProps<'button'>['type'] | 'succeed' | 'warn' | 'gradient' | 'date';
  active?: boolean;
  text?: boolean;
  className?: ComponentProps<'div'>['className'];
  clickable?: boolean;
  borderless?: boolean;
}) {
  return cn(
    'inline-flex overflow-hidden border font-sans transition-colors justify-center font-medium cursor-default p-0 min-w-0',
    buttonHeightAndBorder(size),
    { 'cursor-pointer': !disabled && clickable, 'cursor-not-allowed opacity-20': disabled },

    (type == 'button' || type == null || type == 'date') && {
      'text-slate-600 dark:text-slate-200': text && !active,
      'hover:bg-slate-200 active:bg-slate-300': text && !active && !disabled && clickable,

      'border-slate-200 dark:border-slate-700': !text,
      'bg-slate-50 text-slate-600 dark:bg-slate-800 dark:text-slate-200': !text,
      'bg-slate-300 text-slate-600 dark:text-slate-200 dark:bg-slate-500': active,
      'hover:bg-slate-100 active:bg-slate-200': !text && !disabled && clickable,
      'hover:bg-slate-400 active:bg-slate-500 hover:text-slate-200': active && !disabled && clickable,
      'dark:hover:bg-slate-700 dark:active:bg-slate-600': !disabled && clickable,
    },
    type == 'submit' && {
      'text-astronaut-600 dark:text-astronaut-200': text,
      'hover:bg-astronaut-200 hover:text-astronaut-600 active:bg-astronaut-300': text && !disabled && clickable,

      'border-astronaut-700 dark:border-astronaut-500': !text,
      'bg-astronaut-600 text-astronaut-50 dark:bg-astronaut-700 dark:text-astronaut-50': !text || active,
      'hover:bg-astronaut-700 active:bg-astronaut-800': !text && !disabled && clickable,
      'hover:bg-astronaut-700 hover:text-astronaut-100 active:bg-astronaut-800': active && !disabled && clickable,
      'dark:hover:bg-astronaut-800 dark:active:bg-astronaut-900': !disabled && clickable,
    },
    type == 'reset' && {
      'text-fair-pink-600 dark:text-fair-pink-200': text,
      'hover:bg-fair-pink-200 hover:text-fair-pink-100 active:bg-fair-pink-300': text && !disabled && clickable,

      'border-fair-pink-700': !text,
      'bg-fair-pink-600 text-fair-pink-50': !text || active,
      'hover:bg-fair-pink-700 active:bg-fair-pink-800': !disabled && clickable,
    },
    type == 'succeed' && {
      'text-mantis-600 dark:text-mantis-200': text,
      'hover:bg-mantis-200 hover:text-mantis-600 active:bg-mantis-300': text && !disabled && clickable,

      'border-mantis-700 dark:border-mantis-500': !text,
      'bg-mantis-600 text-mantis-50 dark:bg-mantis-700 dark:text-mantis-50': !text || active,
      'hover:bg-mantis-700 active:bg-mantis-800': !text && !disabled && clickable,
      'hover:bg-mantis-700 hover:text-mantis-100 active:bg-mantis-800': active && !disabled && clickable,
      'dark:hover:bg-mantis-800 dark:active:bg-mantis-900': !disabled && clickable,
    },
    type == 'gradient' && {
      'text-slate-400 dark:text-slate-400 transition-none': text,
      'bg-linear-to-r hover:from-purple-400 hover:to-zumthor-400 hover:text-white dark:hover:text-white':
        text && !disabled && clickable,
      'bg-linear-to-r from-purple-400 to-zumthor-400 text-white dark:text-slate-100': !text || active,
    },
    type == 'warn' && {
      'text-quarter-spanish-white-600 dark:text-quarter-spanish-white-200': text,
      'hover:bg-quarter-spanish-white-200 hover:text-quarter-spanish-white-600 active:bg-quarter-spanish-white-300':
        text && !disabled && clickable,

      'border-quarter-spanish-white-300 dark:border-quarter-spanish-white-800': !text,
      'bg-quarter-spanish-white-200 text-quarter-spanish-white-800 dark:bg-quarter-spanish-white-200 dark:text-quarter-spanish-white-50':
        !text || active,
      'hover:bg-quarter-spanish-white-300 active:bg-quarter-spanish-white-400': !text && !disabled && clickable,
      'hover:bg-quarter-spanish-white-300 hover:text-quarter-spanish-white-300 active:bg-quarter-spanish-white-400':
        active && !disabled && clickable,
      'dark:hover:bg-quarter-spanish-white-800 dark:active:bg-quarter-spanish-white-900': !disabled && clickable,
    },
    { 'bg-transparent': text == true && active != true },
    { 'border-transparent': text == true || active == true },
    className,
  );
}

export const getBackgroundStyle = (colorValue: ColorValue): React.CSSProperties => {
  if (colorValue.type === 'solid') {
    return { backgroundColor: colorValue.color };
  }

  const stops = colorValue.stops.map((stop) => `${stop.color} ${stop.position}%`).join(', ');

  if (colorValue.gradientType === 'linear') {
    const angle = colorValue.angle ?? 90;
    return { background: `linear-gradient(${angle}deg, ${stops})` };
  }

  return { background: `radial-gradient(circle, ${stops})` };
};

export const isMacLike = () => {
  // Check if the code is running in a browser environment
  if (typeof navigator !== 'undefined') {
    return /(Mac|iPhone|iPod|iPad)/i.test(navigator.platform);
  }
  return false; // Default to false if navigator is not available
};
